// Grape variety classification and naming, shared by the vineyard and
// producer panels: red/white sets (German + international spellings),
// synonym folding, and the preferred display spelling.
// Red vs white classification for the grape chips, German and international
// spellings. Lookup is by lowercased name; anything unmatched renders neutral.
const RED_GRAPES = new Set([
  'spätburgunder', 'blauer spätburgunder', 'pinot noir', 'frühburgunder',
  'dornfelder', 'lemberger', 'blauer lemberger', 'blaufränkisch',
  'portugieser', 'blauer portugieser', 'trollinger', 'blauer trollinger',
  'schwarzriesling', 'müllerrebe', 'pinot meunier', 'regent',
  'st. laurent', 'st laurent', 'saint laurent', 'sankt laurent',
  'dunkelfelder', 'domina', 'acolon', 'samtrot', 'tauberschwarz',
  'heroldrebe', 'helfensteiner', 'merlot', 'cabernet sauvignon',
  'cabernet franc', 'cabernet dorsa', 'cabernet mitos', 'cabernet cubin',
  'syrah', 'shiraz', 'zweigelt', 'blauer zweigelt', 'dakapo', 'deckrot'
]);
const WHITE_GRAPES = new Set([
  'riesling', 'weißer riesling', 'rheinriesling', 'roter riesling',
  'müller-thurgau', 'rivaner', 'silvaner', 'sylvaner', 'grüner silvaner',
  'grauburgunder', 'grauer burgunder', 'ruländer', 'pinot gris', 'pinot grigio',
  'weißburgunder', 'weissburgunder', 'weißer burgunder', 'pinot blanc',
  'chardonnay', 'gewürztraminer', 'traminer', 'roter traminer',
  'kerner', 'bacchus', 'scheurebe', 'sauvignon blanc',
  'muskateller', 'gelber muskateller', 'muscat', 'morio-muskat',
  'elbling', 'weißer elbling', 'gutedel', 'weißer gutedel', 'chasselas',
  'auxerrois', 'huxelrebe', 'ortega', 'faberrebe', 'würzer', 'siegerrebe',
  'nobling', 'johanniter', 'solaris', 'souvignier gris',
  'viognier', 'grüner veltliner', 'goldriesling'
]);

// Synonym groups: German spelling variants and international names that mean
// the same variety, so an OSM tag never renders alongside its curated twin
// (Weißburgunder vs "Weißer Burgunder" vs Pinot Blanc). Matching folds case,
// ß/ss and hyphens/spaces; first entry is the canonical key.
const GRAPE_SYNONYMS = [
  ['Weißburgunder', 'Weißer Burgunder', 'Weissburgunder', 'Pinot Blanc', 'Pinot Bianco'],
  ['Grauburgunder', 'Grauer Burgunder', 'Ruländer', 'Pinot Gris', 'Pinot Grigio'],
  ['Spätburgunder', 'Blauer Spätburgunder', 'Pinot Noir', 'Blauburgunder'],
  ['Frühburgunder', 'Pinot Noir Précoce', 'Pinot Madeleine'],
  ['Schwarzriesling', 'Müllerrebe', 'Pinot Meunier'],
  ['Riesling', 'Weißer Riesling', 'Rheinriesling', 'Riesling Renano'],
  ['Müller-Thurgau', 'Rivaner'],
  ['Silvaner', 'Sylvaner', 'Grüner Silvaner'],
  ['Lemberger', 'Blaufränkisch', 'Limberger', 'Blauer Limberger'],
  ['Trollinger', 'Blauer Trollinger', 'Vernatsch', 'Schiava'],
  ['Portugieser', 'Blauer Portugieser'],
  ['Gutedel', 'Weißer Gutedel', 'Chasselas', 'Fendant'],
  ['Muskateller', 'Gelber Muskateller', 'Muscat Blanc'],
  ['Gewürztraminer', 'Traminer', 'Roter Traminer'],
  ['St. Laurent', 'St Laurent', 'Saint Laurent', 'Sankt Laurent'],
  ['Zweigelt', 'Blauer Zweigelt', 'Rotburger'],
  ['Elbling', 'Weißer Elbling'],
  ['Chardonnay', 'Morillon']
];

export const [grapeCanon, grapeDisplay] = (() => {
  const norm = (v) => String(v).toLowerCase().replace(/ß/g, 'ss').replace(/[-\s]+/g, ' ').trim();
  const keyByNorm = new Map();
  const displayByNorm = new Map();
  for (const group of GRAPE_SYNONYMS) {
    const canonical = norm(group[0]);
    for (const g of group) {
      keyByNorm.set(norm(g), canonical);
      displayByNorm.set(norm(g), group[0]);
    }
  }
  return [
    (name) => keyByNorm.get(norm(name)) || norm(name),
    // Preferred spelling for display: the group's first entry
    // ("Weißer Burgunder" and "Pinot Blanc" both render as Weißburgunder).
    (name) => displayByNorm.get(norm(name)) || String(name)
  ];
})();

export function grapeKind(name) {
  const n = String(name).toLowerCase().trim();
  if (RED_GRAPES.has(n)) return 'red';
  if (WHITE_GRAPES.has(n)) return 'white';
  return 'unknown';
}

// Umlaut-tolerant substring match for search inputs: both sides fold case,
// ß/ss and hyphen/space runs, and umlauts BOTH ways (ä→a and ä→ae), so
// "muller", "mueller" and "Müller" all meet.
const foldBase = (v) => String(v).toLowerCase().replace(/ß/g, 'ss').replace(/[-\s]+/g, ' ').trim();
const foldPlain = (v) => foldBase(v).normalize('NFD').replace(/[̀-ͯ]/g, '');
const foldAe = (v) => foldBase(v).replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue');

export function foldedIncludes(hay, needle) {
  if (!hay || !needle) return false;
  const hs = [foldPlain(hay), foldAe(hay)];
  const ns = [foldPlain(needle), foldAe(needle)];
  return hs.some(h => ns.some(n => h.includes(n)));
}

// Word-start variant: the needle must begin the haystack or one of its
// words. Used for synonym matching so a stray fragment inside an obscure
// synonym doesn't light up its whole group ("ri" hid inside Morillon and
// surfaced Chardonnay).
export function foldedWordStart(hay, needle) {
  if (!hay || !needle) return false;
  const hs = [foldPlain(hay), foldAe(hay)];
  const ns = [foldPlain(needle), foldAe(needle)];
  return hs.some(h => ns.some(n => h.startsWith(n) || h.includes(' ' + n)));
}

// Search matcher for the producer filter: the query matches the grape
// itself as a substring anywhere ("burgunder" keeps working), and its
// synonym-group names at word starts — "pinot gris"/"pinot grigio"/"noir"
// find Grauburgunder/Spätburgunder without fragment false-positives.
export function grapeMatchesQuery(grape, query) {
  if (!grape || !query) return false;
  if (foldedIncludes(grape, query)) return true;
  const canon = grapeCanon(grape);
  const group = GRAPE_SYNONYMS.find(g => grapeCanon(g[0]) === canon);
  return !!group && group.some(name => foldedWordStart(name, query));
}
